import { z } from 'zod';

import { EscrowSettingId, LosId, PropertyId } from '../../BrandedIds';
import {
  EscrowInsuranceType,
  EscrowSettingStatus,
  MortgageInsuranceDiscontinuanceCode,
  MortgageInsuranceFHAProgramType,
  MortgageInsuranceRenewalType,
  MortgageInsuranceType,
  PaymentCadence,
  TaxEscrowType,
} from '../../loan/EscrowSetting';
import { zodTemporalInstant, zodTemporalPlainDate } from '../../Temporal';
import { zodBrandedString, zodBrandedUuid } from '../../utils';
import { ExportableAddress } from '../util/ExportableAddress';

export const ExportableEscrowSetting = z
  .object({
    id: zodBrandedUuid<EscrowSettingId>(),
    loanId: zodBrandedString<LosId>(),

    propertyId: zodBrandedUuid<PropertyId>(),
    escrowed: z.boolean(),
    status: EscrowSettingStatus,
    amount: z.number(),
    payeeName: z.string(),
    payeeAddress: ExportableAddress.optional(),
    updatedAt: zodTemporalInstant,
    type: z.enum(['taxEscrowSetting', 'insuranceEscrowSetting', 'mortgageInsuranceEscrowSetting']),

    // tax
    dueDates: z.array(z.string()).optional(),
    taxType: TaxEscrowType.optional(),
    apn: z.string().optional(),

    // insurance
    insuranceName: z.string().optional(),
    insuranceType: EscrowInsuranceType.optional(),
    agentName: z.string().optional(),
    agentEmail: z.string().optional(),
    agentPhone: z.string().optional(),
    policyNumber: z.string().optional(),
    policyStartDate: zodTemporalPlainDate.optional(),
    policyEndDate: zodTemporalPlainDate.optional(),
    coverageAmount: z.number().optional(),
    forcedPlacedFlag: z.boolean().optional(),

    // mortgage insurance
    mortgageInsuranceType: MortgageInsuranceType.optional(),
    fhaProgramType: MortgageInsuranceFHAProgramType.optional(),
    startDate: zodTemporalPlainDate.optional(),
    midpointDate: zodTemporalPlainDate.optional(),
    paymentCadence: PaymentCadence.optional(),
    certificateId: z.string().optional(),
    poolCertificateNumber: z.string().optional(),
    coveragePercent: z.number().optional(),
    isRefundable: z.boolean().optional(),
    isFinanced: z.boolean().optional(),
    renewalType: MortgageInsuranceRenewalType.optional(),
    cancellationEffectiveDate: zodTemporalPlainDate.optional(),
    cancellationDiscontinuanceCode: MortgageInsuranceDiscontinuanceCode.optional(),
  })
  .meta({ title: 'EscrowSetting' });

export type ExportableEscrowSetting = z.infer<typeof ExportableEscrowSetting>;
